import React from "react";
import {
  FiCalendar,
  FiEdit2,
  FiTrash2,
  FiEye,
  FiActivity,
  FiClipboard,
  FiClock,
  FiAlertCircle,
} from "react-icons/fi";
import { Avaliation, WorkoutPlan } from "../../../types";
import utils from "../../../helpers/utils";

/**
 * Componentes auxiliares para o StudentManagementModal
 */

interface TabButtonProps {
  active: boolean;
  onClick: () => void;
  icon: React.ElementType;
  label: string;
  count?: number;
}

export function TabButton({
  active,
  onClick,
  icon: Icon,
  label,
  count,
}: TabButtonProps) {
  return (
    <button
      onClick={onClick}
      className={`group relative flex flex-shrink-0 items-center justify-center gap-1.5 sm:gap-2 px-3 py-3 sm:px-4 sm:py-3.5 md:px-6 md:py-4 text-xs sm:text-sm md:text-base font-semibold transition-all duration-200 touch-manipulation min-h-[52px] sm:min-h-[48px] md:min-h-[56px] rounded-t-lg sm:rounded-t-xl active:scale-[0.98] whitespace-nowrap ${
        active
          ? "text-purple-600 dark:text-purple-400 bg-purple-50/50 dark:bg-purple-900/20"
          : "text-gray-600 active:text-gray-900 active:bg-gray-100/50 md:hover:text-gray-900 md:hover:bg-gray-100/30 dark:text-gray-400 dark:active:text-gray-200 dark:active:bg-gray-800/50 md:dark:hover:text-gray-200 md:dark:hover:bg-gray-800/30"
      }`}
    >
      <div className="flex items-center justify-center gap-1.5 sm:gap-2">
        <Icon
          className={`w-4 h-4 sm:w-[18px] sm:h-[18px] md:w-5 md:h-5 transition-transform duration-200 ${
            active ? "scale-110" : "group-active:scale-95"
          }`}
        />
        <span className="whitespace-nowrap font-medium">{label}</span>
        {count !== undefined && (
          <span
            className={`flex items-center justify-center min-w-[20px] sm:min-w-[22px] md:min-w-[24px] h-5 sm:h-5 md:h-6 rounded-full px-1.5 sm:px-2 text-[10px] sm:text-xs md:text-sm font-bold transition-all duration-200 ${
              active
                ? "bg-purple-600 text-white dark:bg-purple-500 dark:text-white scale-110"
                : "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
            }`}
          >
            {count}
          </span>
        )}
      </div>
      {active && (
        <div className="absolute bottom-0 left-0 right-0 h-1 sm:h-1.5 bg-gradient-to-r from-purple-600 via-purple-600 to-purple-600 rounded-t-full shadow-sm"></div>
      )}
    </button>
  );
}

interface EmptyStateProps {
  icon: React.ElementType;
  title: string;
  description: string;
}

export function EmptyState({
  icon: Icon,
  title,
  description,
}: EmptyStateProps) {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg sm:rounded-xl md:rounded-2xl border-2 border-dashed border-gray-300 bg-white py-10 sm:py-12 md:py-16 px-4 dark:border-gray-700 dark:bg-boxdark">
      <div className="rounded-xl sm:rounded-2xl bg-gradient-to-br from-purple-100 to-purple-100 p-3 sm:p-4 md:p-6 dark:from-purple-900/30 dark:to-purple-900/30">
        <Icon
          size={32}
          className="sm:w-10 sm:h-10 md:w-12 md:h-12 text-gray-400 dark:text-gray-600"
        />
      </div>
      <p className="mt-3 sm:mt-4 md:mt-5 text-sm sm:text-base md:text-lg font-bold text-gray-900 dark:text-white text-center">
        {title}
      </p>
      <p className="mt-1 sm:mt-1.5 md:mt-2 text-[11px] sm:text-xs md:text-sm text-gray-500 dark:text-gray-400 text-center max-w-md px-2">
        {description}
      </p>
    </div>
  );
}

interface AvaliationCardProps {
  avaliation: Avaliation;
  pesoDisplay: string;
  imcDisplay: string;
  onView: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

export function AvaliationCard({
  avaliation,
  pesoDisplay,
  imcDisplay,
  onView,
  onEdit,
  onDelete,
}: AvaliationCardProps) {
  return (
    <div className="group relative overflow-hidden rounded-lg sm:rounded-xl md:rounded-2xl border border-stroke bg-white p-3 sm:p-4 md:p-6 shadow-sm transition-all md:hover:shadow-lg md:hover:-translate-y-0.5 dark:border-strokedark dark:bg-boxdark">
      <div className="relative">
        {/* Date Header - Mobile First */}
        <div className="mb-2 sm:mb-3 md:mb-4 flex items-center justify-between">
          <div className="flex items-center gap-1.5 sm:gap-2">
            <div className="rounded-md sm:rounded-lg bg-purple-100 p-1 sm:p-1.5 md:p-2 dark:bg-purple-900/30">
              <FiCalendar
                size={12}
                className="sm:w-[14px] sm:h-[14px] md:w-4 md:h-4 text-purple-600 dark:text-purple-400"
              />
            </div>
            <p className="text-xs sm:text-sm md:text-base font-bold text-gray-900 dark:text-white">
              {utils.formatDate(
                new Date(avaliation.date || avaliation.registe || ""),
              )}
            </p>
          </div>
        </div>

        {/* Metrics Grid - Mobile First */}
        <div className="mb-2 sm:mb-3 md:mb-5 grid grid-cols-2 gap-2 sm:gap-3 md:gap-4">
          <div className="rounded-md sm:rounded-lg md:rounded-xl bg-gradient-to-br from-purple-50 to-purple-100 p-2 sm:p-3 md:p-4 dark:from-purple-900/20 dark:to-purple-800/20">
            <p className="text-[9px] sm:text-[10px] md:text-xs font-semibold uppercase tracking-wide text-purple-700 dark:text-purple-400">
              Peso
            </p>
            <p className="mt-0.5 sm:mt-0.5 md:mt-1 text-lg sm:text-xl md:text-2xl font-bold text-purple-900 dark:text-purple-300">
              {pesoDisplay}
            </p>
          </div>
          <div className="rounded-md sm:rounded-lg md:rounded-xl bg-gradient-to-br from-purple-50 to-purple-100 p-2 sm:p-3 md:p-4 dark:from-purple-900/20 dark:to-purple-800/20">
            <p className="text-[9px] sm:text-[10px] md:text-xs font-semibold uppercase tracking-wide text-purple-700 dark:text-purple-400">
              IMC
            </p>
            <p className="mt-0.5 sm:mt-0.5 md:mt-1 text-lg sm:text-xl md:text-2xl font-bold text-purple-900 dark:text-purple-300">
              {imcDisplay}
            </p>
          </div>
        </div>

        {/* Action Buttons - Mobile First */}
        <div className="flex gap-1.5 sm:gap-2">
          <button
            onClick={onView}
            className="flex flex-1 items-center justify-center gap-1 sm:gap-1.5 md:gap-2 rounded-md sm:rounded-lg bg-gradient-to-r from-purple-600 to-purple-700 py-2 sm:py-2 md:py-2.5 text-[11px] sm:text-xs md:text-sm font-semibold text-white transition-all active:scale-95 md:hover:shadow-lg md:hover:scale-105 touch-manipulation"
          >
            <FiEye
              size={14}
              className="sm:w-[14px] sm:h-[14px] md:w-4 md:h-4"
            />
            <span className="hidden sm:inline">Visualizar</span>
            <span className="sm:hidden">Ver</span>
          </button>
          <button
            onClick={onEdit}
            className="flex items-center justify-center rounded-md sm:rounded-lg border-2 border-purple-200 p-1.5 sm:p-2 md:p-2.5 text-purple-600 transition-all active:bg-purple-50 active:scale-95 md:hover:bg-purple-50 md:hover:scale-105 dark:border-purple-800 dark:text-purple-400 dark:active:bg-purple-900/20 md:dark:hover:bg-purple-900/20 touch-manipulation"
            aria-label="Editar avaliação"
          >
            <FiEdit2
              size={14}
              className="sm:w-[14px] sm:h-[14px] md:w-4 md:h-4"
            />
          </button>
          <button
            onClick={onDelete}
            className="flex items-center justify-center rounded-md sm:rounded-lg border-2 border-red-200 p-1.5 sm:p-2 md:p-2.5 text-red-600 transition-all active:bg-red-50 active:scale-95 md:hover:bg-red-50 md:hover:scale-105 dark:border-red-800 dark:text-red-400 dark:active:bg-red-900/20 md:dark:hover:bg-red-900/20 touch-manipulation"
            aria-label="Deletar avaliação"
          >
            <FiTrash2
              size={14}
              className="sm:w-[14px] sm:h-[14px] md:w-4 md:h-4"
            />
          </button>
        </div>
      </div>
    </div>
  );
}

interface WorkoutPlanCardProps {
  plan: WorkoutPlan;
  onEdit: () => void;
  onDelete: () => void;
}

export function WorkoutPlanCard({
  plan,
  onEdit,
  onDelete,
}: WorkoutPlanCardProps) {
  // Verificar se a validade está expirada ou próxima de expirar
  const getValidadeStatus = () => {
    if (!plan.end_date) return null;

    const validadeDate = new Date(plan.end_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    validadeDate.setHours(0, 0, 0, 0);

    const diffDays = Math.floor(
      (validadeDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
    );

    if (diffDays < 0) {
      return { status: "expired", days: diffDays };
    } else if (diffDays <= 7) {
      return { status: "expiring", days: diffDays };
    }
    return { status: "valid", days: diffDays };
  };

  const validadeStatus = getValidadeStatus();

  return (
    <div className="group relative overflow-hidden rounded-lg sm:rounded-xl md:rounded-2xl border border-stroke bg-white p-3 sm:p-4 md:p-6 shadow-sm transition-all md:hover:shadow-lg md:hover:-translate-y-0.5 dark:border-strokedark dark:bg-boxdark">
      <div className="relative">
        {/* Header com Ícone e Título - Mobile First */}
        <div className="mb-2 sm:mb-3 md:mb-4 flex items-start justify-between gap-2 sm:gap-3">
          <div className="flex items-start gap-2 sm:gap-2.5 md:gap-3 flex-1 min-w-0">
            <div className="flex-shrink-0 rounded-lg sm:rounded-xl bg-gradient-to-br from-purple-600 to-purple-600 p-1.5 sm:p-2 md:p-3 text-white shadow-sm">
              <FiClipboard
                size={16}
                className="sm:w-[18px] sm:h-[18px] md:w-6 md:h-6"
              />
            </div>
            <div className="flex-1 min-w-0">
              <h5 className="text-sm sm:text-base md:text-lg font-bold text-gray-900 dark:text-white line-clamp-1">
                {plan.name}
              </h5>
              <div className="mt-0.5 sm:mt-1 flex flex-wrap items-center gap-1 sm:gap-1.5 md:gap-2 text-[9px] sm:text-[10px] md:text-xs text-gray-500 dark:text-gray-400">
                <span className="flex items-center gap-0.5 sm:gap-1 whitespace-nowrap">
                  <FiActivity size={10} className="sm:w-3 sm:h-3" />
                  {plan.exercises?.length || 0} treinos
                </span>
                <span className="hidden sm:inline">•</span>
                <span className="hidden sm:flex items-center gap-0.5 sm:gap-1 whitespace-nowrap">
                  <FiCalendar size={10} className="sm:w-3 sm:h-3" />
                  {utils.formatDate(new Date(plan.created_at || ""))}
                </span>
              </div>
            </div>
          </div>

          {/* Action Buttons - Mobile First */}
          <div className="flex gap-1 sm:gap-1.5 md:gap-2 flex-shrink-0">
            <button
              onClick={onEdit}
              className="flex items-center justify-center rounded-md sm:rounded-lg md:rounded-xl border-2 border-purple-200 p-1.5 sm:p-2 md:p-2.5 text-purple-600 transition-all active:bg-purple-50 active:scale-95 md:hover:bg-purple-50 md:hover:shadow-md md:hover:scale-105 dark:border-purple-800 dark:text-purple-400 dark:active:bg-purple-900/20 md:dark:hover:bg-purple-900/20 touch-manipulation"
              aria-label="Editar plano"
            >
              <FiEdit2
                size={14}
                className="sm:w-4 sm:h-4 md:w-[18px] md:h-[18px]"
              />
            </button>
            <button
              onClick={onDelete}
              className="flex items-center justify-center rounded-md sm:rounded-lg md:rounded-xl border-2 border-red-200 p-1.5 sm:p-2 md:p-2.5 text-red-600 transition-all active:bg-red-50 active:scale-95 md:hover:bg-red-50 md:hover:shadow-md md:hover:scale-105 dark:border-red-800 dark:text-red-400 dark:active:bg-red-900/20 md:dark:hover:bg-red-900/20 touch-manipulation"
              aria-label="Deletar plano"
            >
              <FiTrash2
                size={14}
                className="sm:w-4 sm:h-4 md:w-[18px] md:h-[18px]"
              />
            </button>
          </div>
        </div>

        {/* Informações de Validade - Mobile First */}
        {plan.end_date && (
          <div
            className={`mt-2 sm:mt-3 flex items-center gap-1.5 sm:gap-2 rounded-md sm:rounded-lg md:rounded-xl px-2 py-1.5 sm:px-3 sm:py-2 md:py-2.5 ${
              validadeStatus?.status === "expired"
                ? "bg-red-50 dark:bg-red-900/20"
                : validadeStatus?.status === "expiring"
                  ? "bg-orange-50 dark:bg-orange-900/20"
                  : "bg-blue-50 dark:bg-blue-900/20"
            }`}
          >
            {validadeStatus?.status === "expired" ? (
              <FiAlertCircle
                size={12}
                className="sm:w-[14px] sm:h-[14px] md:w-4 md:h-4 text-red-600 dark:text-red-400 flex-shrink-0"
              />
            ) : (
              <FiClock
                size={12}
                className={`sm:w-[14px] sm:h-[14px] md:w-4 md:h-4 flex-shrink-0 ${
                  validadeStatus?.status === "expiring"
                    ? "text-orange-600 dark:text-orange-400"
                    : "text-blue-600 dark:text-blue-400"
                }`}
              />
            )}
            <div className="flex-1 min-w-0">
              <p
                className={`text-[9px] sm:text-[10px] md:text-xs font-semibold ${
                  validadeStatus?.status === "expired"
                    ? "text-red-700 dark:text-red-400"
                    : validadeStatus?.status === "expiring"
                      ? "text-orange-700 dark:text-orange-400"
                      : "text-blue-700 dark:text-blue-400"
                }`}
              >
                Validade: {utils.formatDate(new Date(plan.end_date))}
              </p>
            </div>
            {validadeStatus?.status === "expired" && (
              <span className="flex-shrink-0 rounded-full bg-red-100 px-1.5 sm:px-2 py-0.5 text-[9px] sm:text-[10px] md:text-xs font-bold text-red-700 dark:bg-red-900/40 dark:text-red-400">
                Expirado
              </span>
            )}
            {validadeStatus?.status === "expiring" && (
              <span className="flex-shrink-0 rounded-full bg-orange-100 px-1.5 sm:px-2 py-0.5 text-[9px] sm:text-[10px] md:text-xs font-bold text-orange-700 dark:bg-orange-900/40 dark:text-orange-400">
                {validadeStatus.days}d
              </span>
            )}
          </div>
        )}

        {/* Data de criação no mobile - Mobile First */}
        <div className="mt-1.5 sm:mt-2 sm:hidden flex items-center gap-0.5 sm:gap-1 text-[9px] sm:text-[10px] text-gray-500 dark:text-gray-400">
          <FiCalendar size={10} />
          <span>
            Criado em {utils.formatDate(new Date(plan.created_at || ""))}
          </span>
        </div>
      </div>
    </div>
  );
}
